import React from 'react';
import { Route, Routes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useDecision } from '@optimizely/react-sdk';

import { FEATURES } from 'shared/app/components/optimizely/optimizely-keys';
import { configSelector } from 'shared/app/shell';
import PageNotFound from 'shared/app/components/page-not-found';
import { currentRouteSelector } from 'shared/app/state/selectors/routes';

import RewardsPage from './rewards-page';
import SR6RewardsPage from './sr6-rewards-pages/rewards-page';
import ActiveExternal from './offers-opt-in/active-external';
import ActiveChoice from './offers-opt-in/active-choice';
import ActiveMultiplier from './offers-opt-in/active-multiplier';
import OfferOptInError from './offers-opt-in/offer-opt-in-error';
import Expired from './offers-opt-in/expired';
import ExpiredMultiplier from './offers-opt-in/expired-multiplier';
import OptInSuccess from './offers-opt-in/success';
import OptInSuccessMultiplier from './offers-opt-in/success-multiplier';
import PartnershipOverview from './partnerships';
import LinkSuccess from './partnerships/marriott-result-pages/link-success';
import UnlinkSuccess from './partnerships/marriott-result-pages/unlink-success';
import MarriottError from './partnerships/marriott-result-pages/error';
import NeverLinked from './partnerships/marriott-result-pages/never-linked';
import AlreadyLinked from './partnerships/marriott-result-pages/already-linked';
import RedirectPage from './partnerships/redirect';
import PromoCodes from './promo-codes';
import UnauthRewardsPage from './sr6-rewards-pages/unauth-page';

export const RewardsRoutesHandler = () => {
  /*
   * The `useLocation` hook from `react-router-dom` can sometimes get out of sync with `history.location`.
   * Therefore, we use the `currentRouteSelector` instead.
   * BUG: https://github.com/remix-run/react-router/issues/11473
   */
  const location = useSelector(currentRouteSelector);
  const config = useSelector(configSelector);
  const isMarriottEnabled = config.enableMarriott;
  const [nextGenLoyaltyEnabled] = useDecision(FEATURES.NEXT_GEN_LOYALTY);

  return (
    <Routes location={location}>
      {nextGenLoyaltyEnabled?.enabled ? (
        <React.Fragment>
          <Route element={<SR6RewardsPage />} path="/rewards/my-rewards" />
          <Route element={<UnauthRewardsPage />} path="/rewards" />
        </React.Fragment>
      ) : (
        <Route element={<RewardsPage />} path="/rewards" />
      )}

      <Route element={<PromoCodes />} path="/rewards/promo/redeem" />
      <Route path="/rewards/promo/opt-in">
        <Route element={<ActiveExternal />} path="active-external" />
        <Route element={<ActiveChoice />} path="active-choice" />
        <Route element={<ActiveMultiplier />} path="active-multiplier" />
        <Route element={<OfferOptInError />} path="error" />
        <Route element={<Expired />} path="expired" />
        <Route element={<ExpiredMultiplier />} path="expired-multiplier" />
        <Route element={<OptInSuccess />} path="success" />
        <Route element={<OptInSuccessMultiplier />} path="success-multiplier" />
        {/* Any undefined subroute will return the promo opt-in error page */}
        <Route element={<OfferOptInError />} path="*" />
      </Route>
      {isMarriottEnabled ? (
        <Route path="/rewards/partnerships/marriottbonvoy">
          <Route element={<PartnershipOverview />} path="" />
          <Route element={<LinkSuccess />} path="success" />
          <Route element={<UnlinkSuccess />} path="unlinked" />
          <Route element={<MarriottError />} path="error" />
          <Route element={<NeverLinked />} path="not-linked" />
          <Route element={<AlreadyLinked />} path="already-linked" />
          <Route element={<RedirectPage />} path="redirect" />
          {/* Any undefined subroute will return the MarriottBonvoy error page */}
          <Route element={<MarriottError />} path="*" />
        </Route>
      ) : (
        <Route
          element={<PageNotFound />}
          path="/rewards/partnerships/marriottbonvoy/*"
        />
      )}
    </Routes>
  );
};

export default RewardsRoutesHandler;
